import { useTranslation } from 'react-i18next'
import { useHasFeature, useSelectedAccountId } from '../../hooks/useSubscription'
import { useState } from 'react'
import { api } from '../../services/api'
import FeatureLockedModal from '../Subscription/FeatureLockedModal'

const LANGUAGES = [
  // Base Languages (included in all plans)
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', group: 'europe' },
  { code: 'en', name: 'English', flag: '🇬🇧', group: 'europe' },
  // European Languages (addon)
  { code: 'fr', name: 'Français', flag: '🇫🇷', group: 'europe' },
  { code: 'es', name: 'Español', flag: '🇪🇸', group: 'europe' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', group: 'europe' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱', group: 'europe' },
  { code: 'pt', name: 'Português', flag: '🇵🇹', group: 'europe' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱', group: 'europe' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪', group: 'europe' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴', group: 'europe' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰', group: 'europe' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮', group: 'europe' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷', group: 'europe' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿', group: 'europe' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺', group: 'europe' },
  { code: 'ro', name: 'Română', flag: '🇷🇴', group: 'europe' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', group: 'europe' },
  { code: 'hr', name: 'Hrvatski', flag: '🇭🇷', group: 'europe' },
  { code: 'sk', name: 'Slovenčina', flag: '🇸🇰', group: 'europe' },
  { code: 'sl', name: 'Slovenščina', flag: '🇸🇮', group: 'europe' },
  { code: 'lt', name: 'Lietuvių', flag: '🇱🇹', group: 'europe' },
  { code: 'lv', name: 'Latviešu', flag: '🇱🇻', group: 'europe' },
  { code: 'et', name: 'Eesti', flag: '🇪🇪', group: 'europe' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', group: 'europe' },

  // Asian Languages (addon)
  { code: 'ja', name: '日本語', flag: '🇯🇵', group: 'asia' },
  { code: 'ko', name: '한국어', flag: '🇰🇷', group: 'asia' },
  { code: 'zh', name: '中文', flag: '🇨🇳', group: 'asia' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳', group: 'asia' }
]

const GROUPED_LANGUAGES = LANGUAGES.reduce((acc, lang) => {
  if (!acc[lang.group]) acc[lang.group] = []
  acc[lang.group].push(lang)
  return acc
}, {})

// Helper component to check individual language access
function LanguageButton({ lang, language, accountId, onLanguageClick }) {
  const { hasFeature } = useHasFeature(`language_${lang.code}`, accountId)
  const isSelected = language === lang.code

  return (
    <button
      type="button"
      onClick={() => onLanguageClick(lang.code, lang.name, hasFeature)}
      disabled={!hasFeature && !isSelected}
      className={`
        relative rounded-lg border-2 p-4 text-left transition-all
        ${isSelected
          ? 'border-primary-600 bg-primary-50 shadow-sm'
          : hasFeature
            ? 'border-gray-200 hover:border-gray-300 bg-white'
            : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
        }
      `}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{lang.flag}</span>
          <p className={`text-sm font-medium ${
            isSelected ? 'text-primary-900' : hasFeature ? 'text-gray-900' : 'text-gray-500'
          }`}>
            {lang.name}
          </p>
        </div>
        {isSelected ? (
          <svg className="h-5 w-5 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        ) : !hasFeature ? (
          <svg className="h-5 w-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        ) : null}
      </div>
    </button>
  )
}

function LanguagePreferenceSection({ language, accountId: accountIdProp, onLanguageChange }) {
  const { t } = useTranslation('settings')
  const hookAccountId = useSelectedAccountId()
  const accountId = accountIdProp || hookAccountId
  const [showFeatureLocked, setShowFeatureLocked] = useState(false)
  const [lockedLanguage, setLockedLanguage] = useState(null)
  const [saveStatus, setSaveStatus] = useState(null) // 'saving' | 'saved' | 'error'

  const handleLanguageClick = async (langCode, langName, hasAccess) => {
    if (!hasAccess) {
      setLockedLanguage({ code: langCode, name: langName })
      setShowFeatureLocked(true)
      return
    }

    if (langCode === language) return

    // Auto-save language immediately
    onLanguageChange(langCode)
    setSaveStatus('saving')
    try {
      await api.updateBrandSettings(accountId, { language: langCode })
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus(null), 2000)
    } catch (error) {
      console.error('Failed to auto-save language:', error)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus(null), 3000)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900">{t('language.title')}</h2>
          {saveStatus === 'saving' && (
            <span className="text-sm text-gray-400">{t('language.saving', 'Speichern...')}</span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('language.saved', 'Gespeichert')}
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-sm text-red-600">{t('language.saveError', 'Fehler beim Speichern')}</span>
          )}
        </div>
        <p className="text-gray-600 mt-1">
          {t('language.description')}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {t('language.autoSaveHint', 'Wird sofort gespeichert. Beeinflusst Markenprofil und Tonalitaetsbeispiele.')}
        </p>
      </div>

      {Object.entries(GROUPED_LANGUAGES).map(([groupName, languages]) => (
        <div key={groupName}>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
            {t(`language.groups.${groupName}`)}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {languages.map((lang) => (
              <LanguageButton
                key={lang.code}
                lang={lang}
                language={language}
                accountId={accountId}
                onLanguageClick={handleLanguageClick}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium">{t('language.infoTitle')}</p>
            <p className="text-blue-700 mt-1">
              {t('language.infoDescription')
                .split('<link>')
                .map((part, i) =>
                  i === 0 ? part : (
                    <>
                      <a href="/subscription" className="font-medium underline hover:text-blue-900">
                        {part.split('</link>')[0]}
                      </a>
                      {part.split('</link>')[1]}
                    </>
                  )
                )}
            </p>
          </div>
        </div>
      </div>

      {/* Feature Locked Modal */}
      {showFeatureLocked && lockedLanguage && (
        <FeatureLockedModal
          isOpen={showFeatureLocked}
          onClose={() => {
            setShowFeatureLocked(false)
            setLockedLanguage(null)
          }}
          featureName={`${lockedLanguage.flag} ${lockedLanguage.name} Language`}
          featureDescription={`Generate captions in ${lockedLanguage.name} for your Instagram Stories. Unlock this language to create multilingual content for a global audience.`}
        />
      )}
    </div>
  )
}

export default LanguagePreferenceSection
